import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import TopBar from '../components/TopBar';
import ProjectSelector from '../components/ProjectSelector';
import DataCollectionMethods from '../components/DataCollectionMethods';
import PreviewPanel from '../components/PreviewPanel';
import ActionBar from '../components/ActionBar';
import LoadingSpinner from '../components/LoadingSpinner';

const DataCollection = () => {
  const [searchParams] = useSearchParams();
  const [currentProject, setCurrentProject] = useState(null);
  const [businessProfile, setBusinessProfile] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const projectKey = searchParams.get('projectKey');
    if (projectKey) {
      loadProjectByKey(projectKey);
    }
  }, [searchParams]);

  const loadProjectByKey = async (projectKey) => {
    try {
      setLoading(true);
      const response = await fetch(`/api/projects/by-key/${projectKey}`);
      if (response.ok) {
        const project = await response.json();
        setCurrentProject(project);
      }
    } catch (err) {
      setError('Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const handleDataImported = (profile, reviewsData, mediaData) => {
    setBusinessProfile(profile);
    setReviews(reviewsData || []);
    setMedia(mediaData || []);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TopBar currentProject={currentProject} />
      
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <ProjectSelector 
            currentProject={currentProject}
            onProjectSelect={setCurrentProject}
          />

          {error && (
            <div className="mt-4 rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          {!currentProject ? (
            <div className="mt-8 text-center py-12">
              <p className="text-gray-500 text-lg">
                Select or create a project to start collecting data.
              </p>
            </div>
          ) : (
            <>
              <DataCollectionMethods 
                currentProject={currentProject}
                onDataImported={handleDataImported}
              />

              {businessProfile && (
                <PreviewPanel 
                  businessProfile={businessProfile}
                  reviews={reviews}
                  media={media}
                />
              )}
            </>
          )}
        </motion.div>
      </main>

      {currentProject && (
        <ActionBar currentProject={currentProject} />
      )}
    </div>
  );
};

export default DataCollection;